// «Πιάτα ημέρας» — backend routes/dailyDishes.js (migration 278). Ποτέ 0 €: ο server απαντά 400/409 με
// ελληνικό μήνυμα (error) και code — η οθόνη δείχνει το μήνυμα όπως είναι.

import { API_BASE_URL } from '../config/api';

export interface DailyDish {
  id: number;
  name: string;
  vat: number | null;
  /** Η τιμή του κύριου καναλιού του καταστήματος (τραπέζια αν έχει, αλλιώς take away), σε €. */
  price: number | null;
  prices: { take_away: number | null; delivery: number | null; dine_in: number | null };
  /** Διαφορετική τιμή ανά κανάλι — η αλλαγή τιμής τις κάνει μία. */
  pricesDiffer: boolean;
  today: boolean;
  /** Διαθέσιμο στον κατάλογο (όχι «μη διαθέσιμο» / κρυφό). */
  available: boolean;
  canToday: boolean;
  todayBlockedReason: string | null;
  canEditPrice: boolean;
  priceBlockedReason: string | null;
  lastUsedAt: string | null;
}

export interface DailyLibrary {
  enabled: boolean;
  category: { id: number; name: string };
  dishes: DailyDish[];
  todayCount: number;
  canCreate: boolean;
  defaultVat: number;
  vatRates: number[];
  maxPrice: number;
}

export interface DailySettingsCategory {
  id: number;
  name: string;
  parentId: number | null;
  parentName: string | null;
  productCount: number;
}

export interface DailySettings {
  migrated: boolean;
  enabled: boolean;
  categoryId: number | null;
  categoryMissing: boolean;
  central: boolean;
  categories: DailySettingsCategory[];
}

export class DailyDishesApiError extends Error {
  status: number;
  code: string | null;
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>) {
    super(String(body.error || body.message || `HTTP ${status}`));
    this.status = status;
    this.code = typeof body.code === 'string' ? body.code : null;
    this.body = body;
  }
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/api${path}`, {
    method: init.method || 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  const body = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  if (!res.ok) throw new DailyDishesApiError(res.status, body);
  return body as unknown as T;
}

const base = (storeId: number) => `/stores/${storeId}/daily-dishes`;

export const dailyDishesApi = {
  library: (storeId: number) => call<DailyLibrary>(base(storeId)),
  setToday: (storeId: number, productId: number, today: boolean) =>
    call<{ dish: DailyDish; productIds: number[] }>(`${base(storeId)}/${productId}/today`, { method: 'PUT', body: { today } }),
  setPrice: (storeId: number, productId: number, price: string) =>
    call<{ dish: DailyDish }>(`${base(storeId)}/${productId}/price`, { method: 'PUT', body: { price } }),
  create: (storeId: number, body: { name: string; price: string; vat: number; today: boolean }) =>
    call<{ dish: DailyDish }>(base(storeId), { method: 'POST', body }),
  clearToday: (storeId: number) => call<{ cleared: number; productIds: number[] }>(`${base(storeId)}/clear-today`, { method: 'POST', body: {} }),
  settings: (storeId: number) => call<DailySettings>(`${base(storeId)}/settings`),
  updateSettings: (storeId: number, body: { enabled?: boolean; categoryId?: number | null }) =>
    call<DailySettings>(`${base(storeId)}/settings`, { method: 'PUT', body }),
  createCategory: (storeId: number) =>
    call<{ category: { id: number; name: string }; settings: DailySettings }>(`${base(storeId)}/settings/category`, { method: 'POST', body: {} }),
};

/**
 * «12,50» / «12.5» / «7» → λεπτά· null για ό,τι δεν είναι θετικό ποσό ως 2 δεκαδικά (και το 0: ένα πιάτο
 * ημέρας δεν έχει ποτέ τιμή 0 €). Ίδιος κανόνας με τον server (services/dailyDishes parsePriceCents).
 */
export function parsePriceInput(raw: string): number | null {
  const s = String(raw || '').trim().replace(/\s+/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const cents = Math.round(Number(s) * 100);
  return Number.isFinite(cents) && cents > 0 ? cents : null;
}

/** Λεπτά → «12.50» για το API. */
export const centsToApi = (cents: number) => (cents / 100).toFixed(2);

/** 12.5 → «12,50 €». */
export const euro = (value: number | null | undefined) => (value == null
  ? '—'
  : `${value.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

/** Σύγκριση για την αναζήτηση: χωρίς τόνους/πεζά-κεφαλαία (ΚΟΥΛΟΎΡΙ = κουλουρι). */
export const searchKey = (s: string) => String(s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLocaleLowerCase('el-GR').replace(/ς/g, 'σ').trim();
